import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { getSharingPermissions, normalizeSharing } from "lib/permissions";
import useCurrentOwner from "components/FileManager/hooks/useCurrentOwner";

const PortfoliosContext = createContext(null);

function trimToEmpty(value) {
  return String(value ?? "").trim();
}

async function resolveTenantIdForOrg(orgId) {
  if (!orgId) return null;

  const { data, error } = await supabase
    .from("orgs")
    .select("tenant_id")
    .eq("id", orgId)
    .single();

  if (error) {
    throw new Error(error.message || "Unable to resolve tenant for organization.");
  }

  return data?.tenant_id || null;
}

export function PortfoliosProvider({ children }) {
  const { profile, selectedOrg } = useProfile();

  const [currentId, setCurrentId] = useState(null);
  const [currentName, setCurrentNameState] = useState("");
  const [loadedName, setLoadedName] = useState("");
  const [createdBy, setCreatedBy] = useState(profile?.id || "");
  const currentOwner = useCurrentOwner(createdBy);
  const [sharing, setSharingState] = useState("user");
  const [loadedSharing, setLoadedSharing] = useState("user");

  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState("");

  const selectedOrgId = selectedOrg?.id || null;

  const isDirty = useMemo(() => {
    if (!currentId && trimToEmpty(currentName)) return true;
    return (
      trimToEmpty(currentName) !== trimToEmpty(loadedName) ||
      normalizeSharing(sharing) !== normalizeSharing(loadedSharing)
    );
  }, [currentId, currentName, loadedName, loadedSharing, sharing]);

  const permissions = getSharingPermissions(
    { id: currentId, created_by: createdBy, sharing },
    profile
  );
  const { canEdit, canDelete, canChangeSharing, isReadOnly } = permissions;

  const setCurrentName = useCallback((value) => {
    setCurrentNameState(value);
  }, []);

  const setSharing = useCallback((value) => {
    setSharingState(normalizeSharing(value));
  }, []);

  const resetPortfolio = useCallback(() => {
    setCurrentId(null);
    setCurrentNameState("");
    setLoadedName("");
    setCreatedBy(profile?.id || "");
    setSharingState("user");
    setLoadedSharing("user");
    setLastError("");
    setIsSaving(false);
  }, [profile?.id]);

  const loadPortfolio = useCallback(async (id) => {
    if (!id || !selectedOrgId) return null;

    setLastError("");

    const { data, error } = await supabase
      .from("fp_portfolios")
      .select("id, org_id, tenant_id, created_by, name, sharing, created_at, updated_at")
      .eq("id", id)
      .eq("org_id", selectedOrgId)
      .single();

    if (error) {
      setLastError(error.message || "Unable to load portfolio.");
      return null;
    }

    setCurrentId(data.id);
    setCurrentNameState(data.name || "");
    setLoadedName(data.name || "");
    setCreatedBy(data.created_by || "");
    setSharingState(normalizeSharing(data.sharing));
    setLoadedSharing(normalizeSharing(data.sharing));

    return data;
  }, [selectedOrgId]);

  const savePortfolio = useCallback(async () => {
    const name = trimToEmpty(currentName);

    if (!selectedOrgId || !profile?.id) {
      setLastError("Select an organization before saving.");
      return { ok: false };
    }

    if (!name) {
      setLastError("Portfolio name is required.");
      return { ok: false };
    }

    if (currentId && !canEdit) {
      setLastError("You do not have permission to save this portfolio.");
      return { ok: false };
    }

    const normalizedSharing =
      !currentId || canChangeSharing
        ? normalizeSharing(sharing)
        : normalizeSharing(loadedSharing);

    setIsSaving(true);
    setLastError("");

    try {
      if (currentId) {
        const { data, error } = await supabase
          .from("fp_portfolios")
          .update({ name, sharing: normalizedSharing })
          .eq("id", currentId)
          .eq("org_id", selectedOrgId)
          .select("id, org_id, tenant_id, created_by, name, sharing, created_at, updated_at")
          .single();

        if (error) throw error;

        setCurrentId(data.id);
        setCurrentNameState(data.name || "");
        setLoadedName(data.name || "");
        setCreatedBy(data.created_by || "");
        setSharingState(normalizeSharing(data.sharing));
        setLoadedSharing(normalizeSharing(data.sharing));

        return { ok: true, data };
      }

      const tenantId = await resolveTenantIdForOrg(selectedOrgId);

      if (!tenantId) {
        throw new Error("Selected organization is missing tenant_id.");
      }

      const { data, error } = await supabase
        .from("fp_portfolios")
        .insert({
          org_id: selectedOrgId,
          tenant_id: tenantId,
          created_by: profile.id,
          name,
          sharing: normalizedSharing,
        })
        .select("id, org_id, tenant_id, created_by, name, sharing, created_at, updated_at")
        .single();

      if (error) throw error;

      setCurrentId(data.id);
      setCurrentNameState(data.name || "");
      setLoadedName(data.name || "");
      setCreatedBy(data.created_by || "");
      setSharingState(normalizeSharing(data.sharing));
      setLoadedSharing(normalizeSharing(data.sharing));

      return { ok: true, data };
    } catch (err) {
      setLastError(err?.message || "Unable to save portfolio.");
      return { ok: false, error: err };
    } finally {
      setIsSaving(false);
    }
  }, [
    canChangeSharing,
    canEdit,
    currentId,
    currentName,
    loadedSharing,
    profile?.id,
    selectedOrgId,
    sharing,
  ]);

  const saveAsPortfolio = useCallback(
    async ({ name } = {}) => {
      const nextName = trimToEmpty(name) || `${trimToEmpty(currentName) || "New Portfolio"} Copy`;

      if (!selectedOrgId) {
        setLastError("Select an organization before saving.");
        return { ok: false };
      }

      if (!profile?.id) {
        setLastError("Missing user context.");
        return { ok: false };
      }

      const normalizedSharing = normalizeSharing(sharing);

      setIsSaving(true);
      setLastError("");

      try {
        const tenantId = await resolveTenantIdForOrg(selectedOrgId);

        if (!tenantId) {
          throw new Error("Selected organization is missing tenant_id.");
        }

        const { data, error } = await supabase
          .from("fp_portfolios")
          .insert({
            org_id: selectedOrgId,
            tenant_id: tenantId,
            created_by: profile.id,
            name: nextName,
            sharing: normalizedSharing,
          })
          .select("id, org_id, tenant_id, created_by, name, sharing, created_at, updated_at")
          .single();

        if (error) throw error;

        setCurrentId(data.id);
        setCurrentNameState(data.name || "");
        setLoadedName(data.name || "");
        setCreatedBy(data.created_by || "");
        setSharingState(normalizeSharing(data.sharing));
        setLoadedSharing(normalizeSharing(data.sharing));

        return { ok: true, data };
      } catch (err) {
        setLastError(err?.message || "Unable to save portfolio copy.");
        return { ok: false, error: err };
      } finally {
        setIsSaving(false);
      }
    },
    [currentName, profile?.id, selectedOrgId, sharing]
  );

  const deletePortfolio = useCallback(async () => {
    if (!currentId) return { ok: false };

    if (!canDelete) {
      setLastError("You do not have permission to delete this portfolio.");
      return { ok: false };
    }

    setIsSaving(true);
    setLastError("");

    try {
      const { error } = await supabase
        .from("fp_portfolios")
        .delete()
        .eq("id", currentId)
        .eq("org_id", selectedOrgId);

      if (error) throw error;

      resetPortfolio();

      return { ok: true };
    } catch (err) {
      setLastError(
        err?.message ||
          "Unable to delete portfolio. If funds are linked to this portfolio, reassign them first."
      );
      return { ok: false, error: err };
    } finally {
      setIsSaving(false);
    }
  }, [canDelete, currentId, resetPortfolio, selectedOrgId]);

  useEffect(() => {
    resetPortfolio();
  }, [resetPortfolio, selectedOrgId]);

  const value = useMemo(
    () => ({
      currentId,
      currentName,
      createdBy,
      currentOwner,
      sharing,
      setCurrentName,
      setSharing,
      resetPortfolio,
      loadPortfolio,
      savePortfolio,
      saveAsPortfolio,
      deletePortfolio,
      isDirty,
      isSaving,
      canEdit,
      canDelete,
      canChangeSharing,
      isReadOnly,
      lastError,
    }),
    [
      currentId,
      currentName,
      createdBy,
      currentOwner,
      sharing,
      setCurrentName,
      setSharing,
      resetPortfolio,
      loadPortfolio,
      savePortfolio,
      saveAsPortfolio,
      deletePortfolio,
      isDirty,
      isSaving,
      canEdit,
      canDelete,
      canChangeSharing,
      isReadOnly,
      lastError,
    ]
  );

  return (
    <PortfoliosContext.Provider value={value}>
      {children}
    </PortfoliosContext.Provider>
  );
}

export function usePortfolios() {
  const ctx = useContext(PortfoliosContext);
  if (!ctx) {
    throw new Error("usePortfolios must be used within PortfoliosProvider");
  }
  return ctx;
}
